import { useEffect, useRef, useCallback, useState } from 'react'
import { useUser } from '@/app/store/auth'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import type { RecoveryRunbookTask } from '@/types/entities'

const AI_API_BASE = '/api/ai'

interface RunbookAiSessionOptions {
    requestID: number | string
    objectID?: string
    objectDescription?: string | null
    scenarioDescription?: string | null
    tasks: RecoveryRunbookTask[]
    enabled?: boolean
}

interface RunbookAiSessionReturn {
    sessionId: string | null
    isInitialising: boolean
    reportIssue: (task: RecoveryRunbookTask, allTasks: RecoveryRunbookTask[]) => Promise<string | null>
    lastIssueReply: string | null
}

/**
 * Hook that manages an AI assistant session linked to a recovery runbook execution.
 *
 * - On mount (when tasks are available), finds or creates a session with the runbook context.
 * - Exposes `reportIssue` to notify the AI when a task is set to "Issue" status.
 * - The conversation is persisted and linked to the runbook so it is reused across page loads.
 */
export function useRunbookAiSession({
    requestID,
    objectID,
    objectDescription,
    scenarioDescription,
    tasks,
    enabled = true,
}: RunbookAiSessionOptions): RunbookAiSessionReturn {
    const user = useUser()
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [isInitialising, setIsInitialising] = useState(false)
    const [lastIssueReply, setLastIssueReply] = useState<string | null>(null)
    const initRef = useRef(false)
    const tasksRef = useRef(tasks)
    tasksRef.current = tasks

    // Initialise session when tasks first become available
    useEffect(() => {
        if (!enabled || !requestID || tasks.length === 0 || initRef.current) return
        initRef.current = true

        const init = async () => {
            setIsInitialising(true)
            try {
                const runbookJson = tasks.map(t => ({
                    taskNo: t.taskNo,
                    task: t.task,
                    commands: t.commands,
                    duration: t.duration,
                    dependencies: t.dependencies,
                    taskStatus: t.taskStatus || 'Pending',
                }))

                const res = await fetch(`${AI_API_BASE}/chat/runbook-session`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        engineerId: user?.id ? String(user.id) : 'unknown',
                        runbookRequestId: String(requestID),
                        objectID: objectID || undefined,
                        objectDescription: objectDescription || undefined,
                        scenarioDescription: scenarioDescription || undefined,
                        runbookJson,
                    }),
                })

                if (res.ok) {
                    const data = await res.json()
                    setSessionId(data.sessionId)
                }
            } catch (err) {
                console.error('Failed to initialise runbook AI session:', err)
            } finally {
                setIsInitialising(false)
            }
        }

        init()
    }, [enabled, requestID, tasks.length, objectDescription, scenarioDescription, user?.id])

    // Report an issue to the linked AI session
    const reportIssue = useCallback(
        async (task: RecoveryRunbookTask, allTasks: RecoveryRunbookTask[]): Promise<string | null> => {
            if (!sessionId) return null

            try {
                const runbookStatus = allTasks.map(t => ({
                    taskNo: t.taskNo,
                    task: t.task,
                    taskStatus: t.taskStatus || 'Pending',
                }))

                const res = await fetch(`${AI_API_BASE}/chat/runbook-issue`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        sessionId,
                        task: {
                            taskNo: task.taskNo,
                            task: task.task,
                            commands: task.commands,
                            duration: task.duration,
                            dependencies: task.dependencies,
                            taskStatus: task.taskStatus,
                            taskOwner: task.taskOwner,
                        },
                        runbookStatus,
                    }),
                })

                if (res.ok) {
                    const data = await res.json()
                    setLastIssueReply(data.reply || null)
                    return data.reply || null
                }
            } catch (err) {
                console.error('Failed to report runbook issue to AI:', err)
            }
            return null
        },
        [sessionId]
    )

    return { sessionId, isInitialising, reportIssue, lastIssueReply }
}
